import { Box, Stack } from "@mui/material";
import React, { useContext, useMemo } from "react";
import EyeBrow from '../../commonComponents/eyeBrow/eyeBrow';
import { ThemeContext } from 'styled-components';
import { StyledIcon } from '../../commonComponents/Icon/icon.style';
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import HealthyChip from 'component/commonComponents/HealthyChip';
import { DEVICE_STATUS } from '../../../constants/originalState';
import { PerformanceConfig } from "models/Device";
import Typography from "component/MarketPlace/Typography";
import { TruncatedText } from "component/MarketPlace/Typography/Typography.style";
import { StyledTooltip } from "./pcCard.style";
import { NOT_APPLICABLE } from '../../../constants/originalState';
import { capitalizeFirstLetter } from "utils/functions";

export interface IStyledPCCard {
  primaryDevice: any;
  locale: {
    serialNumber?: any;
    lastScanned?: any;
    eyeBrow?: any;
    commonStatus?: any;
    readyForRefresh?: any;
    deviceHealth?: any;
  }
  cardConfig?: {
    isShowPrimaryLabel?: boolean;
    isShowSerialNumber?: boolean;
    isShowLastScanned?: boolean;
  };
  performanceConfig?: PerformanceConfig;
  localePerformance?: any;
}

const PCCard = (props: IStyledPCCard) => {
  const theme = useContext(ThemeContext);
  const { primaryDevice, locale, cardConfig = {}, performanceConfig, localePerformance} = props;
  const { isShowPrimaryLabel, isShowSerialNumber, isShowLastScanned } = cardConfig;
  const performance = primaryDevice?.tachyon?.HealthStatus?.GradingText;
  const isOrange = [DEVICE_STATUS.INACTIVE].includes(!!primaryDevice?.process_stage?.asset_eyebrow && primaryDevice?.process_stage?.asset_eyebrow?.toUpperCase());
  const eyebrowKey = isOrange ? 'deviceCard' : 'inUse';
  const assetEyebrow = primaryDevice?.process_stage?.asset_eyebrow?.toUpperCase() || '';
  const status = primaryDevice?.process_stage?.stage?.toUpperCase();
  const assetStatus = primaryDevice?.asset_function?.toUpperCase();

  const textAssetFunction = locale?.commonStatus?.[assetStatus!]
    ? ` - ${locale?.commonStatus[assetStatus!]}`
    : '';
  
  const isInactiveStatus = useMemo(() => {
    return assetEyebrow === DEVICE_STATUS.INACTIVE;
  }, [assetEyebrow]);
  
  const toTitleCase = (phrase: string) => {
    return phrase
      ?.toLowerCase()
      .split(" ")
      .map(capitalizeFirstLetter)
      .join(" ");
  };

  const iconEyeBrow = useMemo(() => {
    return status === DEVICE_STATUS.ELIGIBLE_FOR_REFRESH ? (
      <StyledIcon
        styleIcon={{
          fontSize: theme.uptimeFontSizes.desktop.fz5 as unknown as string,
          color: isInactiveStatus ? theme.uptimeColors.deviceCardEyebrow.deviceCard.color : theme.uptimeColors.mainTheme.primary,
          width: theme.uptimeSpace[8],
          height: theme.uptimeSpace[8]
        }}
      >
        <Box display="flex">
          <RefreshOutlinedIcon />
        </Box>
      </StyledIcon>
    ) : <></>;
  }, [isInactiveStatus, status, theme]);

  const eyeBrowText = useMemo(() => {
    const isEligibleForRefresh = status === DEVICE_STATUS.ELIGIBLE_FOR_REFRESH;
    const assetEyebrowText: string = !!assetEyebrow
      ? toTitleCase(locale?.commonStatus?.[assetEyebrow])
      : toTitleCase(locale?.commonStatus?.[DEVICE_STATUS?.IN_USE]);

    if (isShowPrimaryLabel && isEligibleForRefresh) {
      return `${locale?.readyForRefresh}${textAssetFunction}`;
    }

    if (isShowPrimaryLabel && !isEligibleForRefresh) {
      return `${assetEyebrowText}${textAssetFunction}`;
    }

    if (!isShowPrimaryLabel && isEligibleForRefresh) {
      return `${locale?.readyForRefresh} - ${assetEyebrowText}`;
    }

    if (!isShowPrimaryLabel && !isEligibleForRefresh) {
      return `${assetEyebrowText}`;
    }

    return '';
  }, [status, assetEyebrow, locale, isShowPrimaryLabel, textAssetFunction]);

  return (
    <Box pt="14px" minHeight={214}>
      <Box
        component="img"
        src={primaryDevice?.model_picture}
        height={80}
        mx="auto"
        alt={`${!!primaryDevice?.model && primaryDevice?.model}`}
        title={`${!!primaryDevice?.model && primaryDevice?.model} ${!!primaryDevice?.manufacturer && primaryDevice?.manufacturer} ${!!primaryDevice?.serial_number && primaryDevice?.serial_number}`}
      />
      <Box mt="6px">
        <Stack direction="row" alignItems="center" gap="16px">
          <Box flexBasis='30%'>
          <StyledTooltip title={!!primaryDevice?.manufacturer? primaryDevice?.manufacturer?.toUpperCase() : NOT_APPLICABLE} arrow>
            <TruncatedText
              maxlines={1}
              variant="overline"
              className="select-all-text"
              sx={{
                textTransform: 'uppercase',
                color: 'text.secondary',
                maxWidth: '200px'
              }}
              id="lbl-pccard-manufacturer"
            >
              {!!primaryDevice?.manufacturer? primaryDevice?.manufacturer?.toUpperCase() : NOT_APPLICABLE}
            </TruncatedText>
          </StyledTooltip>
          </Box>
          {assetEyebrow && (
            <EyeBrow
              color={theme.uptimeColors.deviceCardEyebrow[eyebrowKey].color}
              backgroundColor={theme.uptimeColors.deviceCardEyebrow[eyebrowKey].background}
              text={eyeBrowText}
              icon={iconEyeBrow}
              fontWeight={600}
              id="chp-pccard-status"
            />
          )}
        </Stack>
        <StyledTooltip title={!!primaryDevice?.model? primaryDevice?.model : NOT_APPLICABLE} arrow placement="bottom-start">
          <TruncatedText
            maxlines={1}
            variant="h5"
            className="select-all-text"
            id="lbl-pccard-model"
          >
            {!!primaryDevice?.model? primaryDevice?.model : NOT_APPLICABLE}
          </TruncatedText>
        </StyledTooltip>
      </Box>

      <Stack spacing="5px" mt="5px" color="text.secondary">
        {isShowSerialNumber && (
          <DeviceInfo label={locale?.serialNumber} value={primaryDevice?.serial_number} />
        )}

        {isShowLastScanned && (
          <DeviceInfo label={locale?.lastScanned} value={primaryDevice?.configuration?.lastActive} />
        )}

        {performanceConfig?.isEnabledTachyon && (
          <Stack direction="row" alignItems="center">
            <Typography variant="body2" fontWeight={700} mr="8px" flexShrink={0}>
              {locale?.deviceHealth}
            </Typography>
            <HealthyChip performanceConfig={performanceConfig} performance={performance} localeText={localePerformance} />
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

const DeviceInfo = ({ label, value }: { label: string; value?: string }) => {
  if (!value) return null;
  return (
    <Stack direction="row">
      <Typography variant="body2" fontWeight={700} mr="8px" flexShrink={0} id="lbl-pccard-label">
        {label}
      </Typography>
      <StyledTooltip title={value} arrow>
        <TruncatedText maxlines={1} variant="body2" className="select-all-text" id="lbl-pccard-value">
          {value}
        </TruncatedText>
      </StyledTooltip>
    </Stack>
  );
};

export default PCCard;
